import { lazy, Suspense, useEffect, useState } from "react";
import { useIntl } from "react-intl";
import { useNavigate, useParams } from "react-router";
import { ErrorBox, NotFound, PreviewBar, useNoIndex } from "../components/Layout.js";
import { Drawing, RoundResult, Sketch } from "../components/lazy.js";
import { TextsProvider } from "../components/texts.js";
import { Tone } from "../components/tone.js";
import { ApiError, api } from "../data/api.js";
import { device, sha256Hex } from "../data/device.js";

// Stránka náhledu `/{jazyk}/preview/{token}` (REQ-031): podepsaný odkaz z MCP `preview_url`.
// Průchod založí sezení `preview` nad verzí a pustí stejné obrazovky jako u uživatele
// (rozpracované sezení téhož odkazu se v zařízení použije znovu). Mezivýsledek kola a Skica se
// počítají ze vstupu odkazu při každém načtení nad aktuálním stavem verze, jen pro čtení. Náhled
// páru (REQ-036) ukáže stránku páru pro dva vstupy a sadu pravidel, i draft, bez odvolání.

type Loaded = Awaited<ReturnType<typeof api.previewResult>>;
type LoadedPair = Awaited<ReturnType<typeof api.previewPair>>;

const PairReady = lazy(() => import("./PairPage.js").then((m) => ({ default: m.PairReady })));

/** Obrazovka z odkazu. Podpis ověří API, tady se jen volí, kam se zeptat. */
export function screenOf(token: string): string {
  try {
    const data = token.split(".")[0] ?? "";
    const json = atob(data.replace(/-/g, "+").replace(/_/g, "/"));
    return (JSON.parse(json) as { s?: string }).s ?? "walkthrough";
  } catch {
    return "walkthrough";
  }
}

export function PreviewPage() {
  const { locale: lang } = useIntl();
  const { token = "" } = useParams();
  const navigate = useNavigate();
  const [status, setStatus] = useState<"loading" | "missing" | "error">("loading");
  const [result, setResult] = useState<Loaded | null>(null);
  const [pair, setPair] = useState<LoadedPair | null>(null);
  useNoIndex();

  useEffect(() => {
    let alive = true;
    const fail = (e: unknown) => {
      if (alive) setStatus(e instanceof ApiError && (e.status === 404 || e.status === 409 || e.status === 422) ? "missing" : "error");
    };
    if (screenOf(token) === "pair") {
      api.previewPair(token).then((r) => alive && setPair(r), fail);
      return () => {
        alive = false;
      };
    }
    if (screenOf(token) !== "walkthrough") {
      api.previewResult(token).then((r) => alive && setResult(r), fail);
      return () => {
        alive = false;
      };
    }
    void (async () => {
      try {
        const key = await sha256Hex(token);
        const existing = await device.previewSession(key);
        const rec = existing === undefined ? undefined : await device.session(existing);
        if (rec !== undefined && rec.done !== true) {
          navigate(`/${lang}/s/${rec.id}`, { replace: true });
          return;
        }
        const s = await api.previewSession(token);
        await device.saveSession({ id: s.session_id, token: s.token, slug: "", locale: s.locale, origin: "preview", createdAt: new Date().toISOString(), answered: 0, total: 0 });
        await device.setPreviewSession(key, s.session_id);
        if (alive) navigate(`/${lang}/s/${s.session_id}`, { replace: true });
      } catch (e) {
        fail(e);
      }
    })();
    return () => {
      alive = false;
    };
  }, [token, lang, navigate]);

  if (status === "missing") return <NotFound />;
  if (status === "error") return <ErrorBox onRetry={() => window.location.reload()} />;
  if (pair !== null) {
    return (
      <>
        <PreviewBar />
        <Tone slug={pair.slug}>
          <Suspense fallback={<Drawing />}>
            <PairReady data={pair} onRevoke={null} />
          </Suspense>
        </Tone>
      </>
    );
  }
  if (result === null) return null;
  return (
    <TextsProvider texts={result.texts} locale={result.locale} preview>
      <PreviewBar />
      <Tone slug={result.view.slug}>
        <Suspense fallback={<Drawing />}>
          {result.screen === "round_result" ? (
            <RoundResult
              round={result.round ?? 1}
              rounds={result.view.rounds}
              payload={result.payload}
              view={result.view}
              portraitSeed={result.portrait_seed ?? null}
              nextItems={0}
              onContinue={() => undefined}
              onLater={() => undefined}
              readOnly
            />
          ) : (
            <Sketch payload={result.payload} view={result.view} pictureUrl={result.picture_url ?? null} portraitSource={{ kind: "preview", token }} owner shareUrl={null} feedback={() => null} onRevoke={null} revoked={false} />
          )}
        </Suspense>
      </Tone>
    </TextsProvider>
  );
}
